import { Button, FlashMessage, Modal } from "../../common";
import { htmlToPlainText } from "../../../utils/helper";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";
import { updateCandidatePersonalValuesRequest } from "../../../reducers/candidate/candidate.reducer";
import EditIcon from "../icons/edit-icon.svg";

interface PersonalValuesProps {
  personal_values: string;
  isPublicPage?: boolean;
  openRequest?: boolean;
  onOpenHandled?: () => void;
}

function PersonalValues({
  personal_values,
  isPublicPage,
  openRequest,
  onOpenHandled,
}: PersonalValuesProps) {
  const initialPersonalValues = htmlToPlainText(personal_values);
  const [personalModalOpen, setPersonalModalOpen] = useState<boolean>(false);
  const [isPersonalValuesExpanded, setIsPersonalValuesExpanded] = useState(false);
  const [localData, setLocalData] = useState<string>(initialPersonalValues);
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<{ personal_values: string }>({
    defaultValues: {
      personal_values: initialPersonalValues,
    },
  });

  const valueLength = watch("personal_values")?.length || 0;

  const candidateViewFlag = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates.candidateViewFlag
  );

  const openPersonalValuesModal = () => {
    if (isPublicPage) return;
    setValue("personal_values", localData);
    setPersonalModalOpen(true);
  };

  useEffect(() => {
    if (!openRequest || isPublicPage) return;
    openPersonalValuesModal();
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, isPublicPage]);

  const closePersonalValuesModal = () => {
    setPersonalModalOpen(false);
  };

  const onSubmit = async (data: { personal_values: string }) => {
    try {
      const payload = {
        personal_values: htmlToPlainText(data.personal_values),
      };

      dispatch(updateCandidatePersonalValuesRequest(payload));
    } catch (error) {
      FlashMessage("Failed to update personal values", "error");
    }
  };

  // Update local data when props change
  useEffect(() => {
    const cleanPersonalValues = htmlToPlainText(personal_values);
    setLocalData(cleanPersonalValues);
    setIsPersonalValuesExpanded(false);
    setValue("personal_values", cleanPersonalValues);
  }, [personal_values, setValue]);

  // Handle API response
  useEffect(() => {
    if (candidateViewFlag === "personal_values_success") {
      FlashMessage("Personal values updated successfully");
      const updatedData = htmlToPlainText(watch("personal_values"));
      setLocalData(updatedData);
      setPersonalModalOpen(false);
    } else if (candidateViewFlag === "personal_values_failed") {
      FlashMessage("Failed to update personal values", "error");
    }
  }, [candidateViewFlag, watch]);

  return (
    <div className="accordion" id="profile-values">
      <div className="accordion-head">
        <b>Personal Values</b>
        {!isPublicPage &&
          (!_.isEmpty(localData) ? (
            <div className="edit-icon" onClick={openPersonalValuesModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openPersonalValuesModal}>+Add</span>
          ))}
      </div>
      {!_.isEmpty(localData) && (
        <div className="accordion-body">
          <div className="bordered-content">
            <p className="read-more-less">
              {isPersonalValuesExpanded || localData.length <= 200
                ? localData
                : `${localData.slice(0, 200)}...`}
              {localData.length > 200 && (
                <Button
                  type="button"
                  variant="text"
                  color="none"
                  className="btn-link-btn read-more-less-link"
                  onClick={() => setIsPersonalValuesExpanded((expanded) => !expanded)}
                >
                  {isPersonalValuesExpanded ? " Show less" : " See more"}
                </Button>
              )}
            </p>
          </div>
        </div>
      )}
      {!isPublicPage && (
        <Modal
          visible={personalModalOpen}
          onClose={closePersonalValuesModal}
          className={`candidate-profile-modal`}
          title={
            !_.isEmpty(localData) ? "Edit Personal Values" : "Personal Values"
          }
        >
          <div className="modal-body-wrapper">
            <form onSubmit={handleSubmit(onSubmit)} className="form-wrapper">
              <div className="form-group">
                <label htmlFor="personal_values" className="form-label">
                  Personal Values
                </label>

                <textarea
                  {...register("personal_values", {
                    maxLength: {
                      value: 1000,
                      message: "Cannot exceed more than 1000 characters",
                    },
                    required: {
                      value: true,
                      message: "Personal values can't be empty",
                    },
                  })}
                  placeholder="Enter your personal values"
                  className="form-control"
                  rows={4}
                />
                <div className="count-wrapper">
                  <p className="error-helper">
                    {errors.personal_values && errors.personal_values.message}
                  </p>
                  <div className="count">{valueLength}/1000</div>
                </div>
              </div>

              <div className="btn-wrapper end">
                <Button className="save-btn" type="submit">
                  Save
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default PersonalValues;
